/**
 * Assessment Capture Service
 * =====================================================================
 * Singleton responsible for in-memory state management of assessment
 * data. Handles all setters and getters for assessment phase data.
 * =====================================================================
 */

import type { RANResult, RANResults, ClinicalComposite } from '../clinicalScoringService';
import type { DysTestScoringResult } from '../scoringEngine/dysTestScoringEngine';
import { clearVisits } from '../storyVisitTracker';
import { getFullscreenRecord, resetFullscreenRecord } from '../../utils/ui/fullscreenSession';
import { clearStoryPoolSelection, getStoryPoolSelection, restoreStoryPoolSelection } from '../storyPoolSelection';
import { mergeCameraFreezeRecords, type CameraFreezeRecord } from '../ocular_analysis/cameraFreeze';
import type {
  UserInformation,
  AllowCameraData,
  CalibrationGameData,
  StoryPageData,
  StoryAnswerPageData,
  FeedbackData,
  WwtWordResult,
  GazeSamplingLog,
} from './types';

class AssessmentCaptureService {
  private static instance: AssessmentCaptureService;
  private assessmentId: string = '';
  private userInfo: UserInformation | null = null;
  private allowCameraData: AllowCameraData | null = null;
  private calibrationGameData: CalibrationGameData | null = null;
  private storyPages: Map<string, StoryPageData> = new Map();
  private storyAnswerPages: Map<string, StoryAnswerPageData> = new Map();
  private pageBackgrounds: Map<string, string> = new Map();
  private ranResults: RANResults = {};
  /** variant → (row index → audio blob) */
  private ranRowBlobs: Map<string, Map<number, Blob>> = new Map();
  private feedbackData: FeedbackData | null = null;
  /** Proctor/parent observation notes (distraction, fatigue, environment) — separate
   *  from feedbackData, which is the candidate's own self-reported feedback text. */
  private observationNotes: string | null = null;
  private completionTimestamp: string | null = null;
  private clinicalComposite: ClinicalComposite | null = null;
  private unifiedScore: DysTestScoringResult | null = null;
  private wordWritingResults: WwtWordResult[] = [];
  /** Gaze points per RAN subtest — each gets its own eye-gaze replay page. */
  private ranGazePointsByVariant: Partial<Record<'letter' | 'digit' | 'color' | 'object', Array<{ x: number; y: number; timestamp: number }>>> = {};
  /**
   * Set only when a *stored* snapshot is loaded back in (admin/dashboard
   * report re-viewing) and that snapshot's gaze arrays were thinned for
   * storage by authService.ts's stripLargeData(). Never set during a live
   * in-progress assessment — the in-memory arrays captured during the
   * session itself are always full-fidelity. Lets report generators
   * recover the true pre-decimation capture rate for quality-gate scoring
   * instead of scoring the thinned array's own (much lower) point density.
   */
  private gazeSamplingMeta: GazeSamplingLog | null = null;
  /** Step 25: freezes saved with a restored session (draft, server); the running camera adds its own. */
  private cameraFreezeRecord: CameraFreezeRecord | null = null;
  private cameraFreezeSource: { get: () => CameraFreezeRecord; reset: () => void } | null = null;

  private static readonly USER_INFO_KEY = 'assessment_user_info';

  private constructor() {
    const savedId = sessionStorage.getItem('assessment_id');
    if (savedId) {
      this.assessmentId = savedId;
    }
    // Restore user info so report downloads work after a Finish-page refresh
    try {
      const raw = sessionStorage.getItem(AssessmentCaptureService.USER_INFO_KEY);
      if (raw) this.userInfo = JSON.parse(raw) as UserInformation;
    } catch { /* corrupt storage — leave null */ }
  }

  /** Persisted ageTier — survives page refresh within the same tab */
  private get persistedAgeTier(): string | null {
    return sessionStorage.getItem('assessment_age_tier');
  }

  public static getInstance(): AssessmentCaptureService {
    if (!AssessmentCaptureService.instance) {
      AssessmentCaptureService.instance = new AssessmentCaptureService();
    }
    return AssessmentCaptureService.instance;
  }

  // ── Setters ───────────────────────────────────────────────────────────

  public setAssessmentId(id: string): void {
    this.assessmentId = id;
    sessionStorage.setItem('assessment_id', id);
  }

  public setUserInformation(data: UserInformation): void {
    const normalized: UserInformation = {
      ...data,
      assessmentLanguage: data.assessmentLanguage ?? data.language,
      assessmentLanguageFluency: data.assessmentLanguageFluency ?? (data.isNativeLanguage ? 'native' : 'unknown'),
      ...(data.homeLanguage || data.nativeLanguage || data.isNativeLanguage
        ? { homeLanguage: data.homeLanguage ?? data.nativeLanguage ?? data.language }
        : {}),
    };
    this.userInfo = normalized;
    if (normalized.ageTier) {
      sessionStorage.setItem('assessment_age_tier', normalized.ageTier);
    }
    // Persist so report downloads work after a Finish-page refresh
    try {
      sessionStorage.setItem(AssessmentCaptureService.USER_INFO_KEY, JSON.stringify(normalized));
    } catch { /* storage full — non-fatal */ }
  }

  public setAllowCameraData(data: AllowCameraData): void {
    this.allowCameraData = data;
  }

  public setCalibrationGameData(data: CalibrationGameData): void {
    this.calibrationGameData = data;
  }

  public addStoryPageData(storyId: string, data: StoryPageData): void {
    this.storyPages.set(storyId, data);
  }

  public addStoryAnswerPageData(pageId: string, data: StoryAnswerPageData): void {
    this.storyAnswerPages.set(pageId, data);
  }

  public setRANResult(result: RANResult): void {
    this.ranResults = { ...this.ranResults, [result.variant]: result };
  }

  public setRanGazePointsForVariant(
    variant: 'letter' | 'digit' | 'color' | 'object',
    pts: Array<{ x: number; y: number; timestamp: number }>,
  ): void {
    this.ranGazePointsByVariant = { ...this.ranGazePointsByVariant, [variant]: pts };
  }

  public clearRANResults(): void {
    this.ranResults = {};
    this.ranRowBlobs.clear();
  }

  public addRANRowBlob(variant: string, row: number, blob: Blob): void {
    if (!this.ranRowBlobs.has(variant)) this.ranRowBlobs.set(variant, new Map());
    this.ranRowBlobs.get(variant)!.set(row, blob);
  }

  public getRANRowBlobs(): Map<string, Map<number, Blob>> {
    return this.ranRowBlobs;
  }

  public addPageBackground(pageId: string, dataUrl: string): void {
    this.pageBackgrounds.set(pageId, dataUrl);
  }

  // ── Getters ───────────────────────────────────────────────────────────

  public getAssessmentId(): string {
    return this.assessmentId;
  }

  public getUserInformation(): UserInformation | null {
    return this.userInfo;
  }

  public getAllowCameraData(): AllowCameraData | null {
    return this.allowCameraData;
  }

  public getCalibrationGameData(): CalibrationGameData | null {
    return this.calibrationGameData;
  }

  public getStoryPageData(storyId: string): StoryPageData | null {
    return this.storyPages.get(storyId) || null;
  }

  public getStoryPagesMap(): Map<string, StoryPageData> {
    return this.storyPages;
  }

  /**
   * Return all visit records for a story, sorted by visitNumber ascending.
   * Matches both legacy key ('story1') and versioned keys ('story1_v1', 'story1_v2', …).
   */
  public getStoryVisits(baseId: string): StoryPageData[] {
    return Array.from(this.storyPages.entries())
      .filter(([key]) => key === baseId || key.startsWith(`${baseId}_v`))
      .sort((a, b) => (a[1].visitNumber ?? 1) - (b[1].visitNumber ?? 1))
      .map(([, data]) => data);
  }

  /**
   * Return all question-page visit records for a story, sorted by visitNumber.
   * Matches keys like 'story1-questions1_v1', 'story1-questions1_v2', etc.
   */
  public getQuestionVisits(baseId: string): StoryAnswerPageData[] {
    return Array.from(this.storyAnswerPages.entries())
      .filter(([key]) => key === baseId || key.startsWith(`${baseId}_v`))
      .sort((a, b) => (a[1].visitNumber ?? 1) - (b[1].visitNumber ?? 1))
      .map(([, data]) => data);
  }

  public getStoryAnswerPages(): StoryAnswerPageData[] {
    return Array.from(this.storyAnswerPages.values());
  }

  public getStoryAnswerPagesMap(): Map<string, StoryAnswerPageData> {
    return this.storyAnswerPages;
  }

  public getPageBackgroundsMap(): Map<string, string> {
    return this.pageBackgrounds;
  }

  public getRANResults(): RANResults {
    return this.ranResults;
  }

  public getRANGazePointsByVariant(): Partial<Record<'letter' | 'digit' | 'color' | 'object', Array<{ x: number; y: number; timestamp: number }>>> {
    return this.ranGazePointsByVariant;
  }

  public setGazeSamplingMeta(meta: GazeSamplingLog | null | undefined): void {
    this.gazeSamplingMeta = meta ?? null;
  }

  public getGazeSamplingMeta(): GazeSamplingLog | null {
    return this.gazeSamplingMeta;
  }

  public setFeedbackData(data: FeedbackData): void {
    this.feedbackData = data;
  }

  public getFeedbackData(): FeedbackData | null {
    return this.feedbackData;
  }

  public setObservationNotes(notes: string): void {
    this.observationNotes = notes;
  }

  public getObservationNotes(): string | null {
    return this.observationNotes;
  }

  /** ISO timestamp from the backend's completed_at — set when loading a snapshot from the dashboard. */
  public setCompletionTimestamp(ts: string): void {
    this.completionTimestamp = ts;
  }

  public getCompletionTimestamp(): string | null {
    return this.completionTimestamp;
  }

  public setClinicalComposite(composite: ClinicalComposite): void {
    this.clinicalComposite = composite;
  }

  public getClinicalComposite(): ClinicalComposite | null {
    return this.clinicalComposite;
  }

  /** Additive alongside clinicalComposite — see Phase 1 of the unified scoring engine plan. */
  public setUnifiedScore(score: DysTestScoringResult): void {
    this.unifiedScore = score;
  }

  public getUnifiedScore(): DysTestScoringResult | null {
    return this.unifiedScore;
  }

  public setWordWritingResults(results: WwtWordResult[]): void {
    this.wordWritingResults = results;
  }

  public getWordWritingResults(): WwtWordResult[] {
    return this.wordWritingResults;
  }

  /** Returns assessment tier (1-12) based on the user's ageTier string. Falls back to age number. */
  public getUserTier(): 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 {
    const ageTier = this.userInfo?.ageTier ?? this.persistedAgeTier ?? undefined;
    if (ageTier === '5-7')    return 1;
    if (ageTier === '8-9')    return 2;
    if (ageTier === '10-12')  return 3;
    if (ageTier === '13-14')  return 4;
    if (ageTier === '15-16')  return 5;
    if (ageTier === '17-20')  return 6;
    if (ageTier === '21-28')  return 7;
    if (ageTier === '29-35')  return 8;
    if (ageTier === '36-45')  return 9;
    if (ageTier === '46-55')  return 10;
    if (ageTier === '56-70')  return 11;
    if (ageTier === '71-101') return 12;
    // Fallback for legacy data without ageTier
    const age = this.userInfo?.age ?? 9;
    if (age <= 6)  return 1;
    if (age <= 8)  return 2;
    if (age <= 11) return 3;
    if (age <= 14) return 4;
    if (age <= 16) return 5;
    if (age <= 20) return 6;
    if (age <= 28) return 7;
    if (age <= 35) return 8;
    if (age <= 45) return 9;
    if (age <= 55) return 10;
    if (age <= 70) return 11;
    return 12;
  }

  /** Returns the most recent emotion label from calibration game or allow-camera timeline. */
  public getLastKnownEmotion(): string | undefined {
    const timeline =
      this.calibrationGameData?.emotionTimeline ??
      this.allowCameraData?.emotionTimeline;
    if (!timeline || timeline.length === 0) return undefined;
    return timeline[timeline.length - 1]?.emotion;
  }

  /** Step 25: the running camera's freeze record (registered by backgroundVisionService). */
  public setCameraFreezeSource(get: () => CameraFreezeRecord, reset: () => void): void {
    this.cameraFreezeSource = { get, reset };
  }

  /** Step 25: the freeze record of a restored session. */
  public setCameraFreezeRecord(record: CameraFreezeRecord | null | undefined): void {
    this.cameraFreezeRecord = record ?? null;
  }

  public getCameraFreezeRecord(): CameraFreezeRecord | null {
    return mergeCameraFreezeRecords(this.cameraFreezeRecord, this.cameraFreezeSource?.get());
  }

  public getAllData() {
    return {
      assessmentId: this.assessmentId,
      userInformation: this.userInfo,
      allowCamera: this.allowCameraData,
      calibrationGame: this.calibrationGameData,
      ranResults: this.ranResults,
      ranGazePointsByVariant: this.ranGazePointsByVariant,
      storyPages: Array.from(this.storyPages.entries()).map(([id, data]) => ({ id, data })),
      storyAnswerPages: Array.from(this.storyAnswerPages.entries()).map(([id, data]) => ({ id, data })),
      wordWritingResults: this.wordWritingResults,   // was missing — now included in full ZIP JSON
      pageBackgrounds: Object.fromEntries(this.pageBackgrounds),
      feedbackData: this.feedbackData,
      observationNotes: this.observationNotes,
      fullscreen: getFullscreenRecord(),
      cameraFreeze: this.getCameraFreezeRecord(),
      storyPool: getStoryPoolSelection(),
      exportTimestamp: new Date().toISOString(),
    };
  }

  /**
   * Restore all fields from a previously saved getAllData() snapshot.
   * Used by the draft-resume flow to repopulate the singleton after a
   * tab close / browser restart, before navigating to the resume route.
   * Also repopulates the sessionStorage keys that the assessment pages expect.
   */
  public restoreFromSnapshot(data: ReturnType<AssessmentCaptureService['getAllData']>): void {
    if (data.assessmentId) this.setAssessmentId(data.assessmentId);
    if (data.userInformation) this.setUserInformation(
      data.userInformation as Parameters<typeof this.setUserInformation>[0],
    );
    if (data.allowCamera) this.allowCameraData =
      data.allowCamera as typeof this.allowCameraData;
    if (data.calibrationGame) this.calibrationGameData =
      data.calibrationGame as typeof this.calibrationGameData;
    if (data.ranResults && typeof data.ranResults === 'object') {
      this.ranResults = data.ranResults as typeof this.ranResults;
    }
    if (data.ranGazePointsByVariant) {
      this.ranGazePointsByVariant =
        data.ranGazePointsByVariant as typeof this.ranGazePointsByVariant;
    }
    for (const { id, data: sd } of data.storyPages ?? []) {
      this.storyPages.set(id, sd as Parameters<typeof this.addStoryPageData>[1]);
    }
    for (const { id, data: ad } of data.storyAnswerPages ?? []) {
      this.storyAnswerPages.set(id, ad as Parameters<typeof this.addStoryAnswerPageData>[1]);
    }
    if (Array.isArray(data.wordWritingResults)) {
      this.wordWritingResults =
        data.wordWritingResults as typeof this.wordWritingResults;
    }
    if (data.feedbackData) this.feedbackData =
      data.feedbackData as typeof this.feedbackData;
    if (data.observationNotes) this.observationNotes = data.observationNotes;
    this.setCameraFreezeRecord(data.cameraFreeze);
    restoreStoryPoolSelection(data.storyPool);
  }

  public clear(): void {
    this.userInfo = null;
    this.allowCameraData = null;
    this.calibrationGameData = null;
    this.storyPages.clear();
    this.storyAnswerPages.clear();
    this.pageBackgrounds.clear();
    this.ranResults = {};
    this.ranRowBlobs.clear();
    this.ranGazePointsByVariant = {};
    this.gazeSamplingMeta = null;
    this.cameraFreezeRecord = null;
    this.cameraFreezeSource?.reset();
    resetFullscreenRecord();
    clearStoryPoolSelection();
    this.feedbackData = null;
    this.observationNotes = null;
    this.completionTimestamp = null;
    this.clinicalComposite = null;
    this.unifiedScore = null;
    this.wordWritingResults = [];
    this.assessmentId = '';
    sessionStorage.removeItem('assessment_id');
    sessionStorage.removeItem('assessment_age_tier');
    sessionStorage.removeItem(AssessmentCaptureService.USER_INFO_KEY);
    clearVisits();
  }

  public getSystemInformation() {
    const ua = navigator.userAgent;
    const screen = window.screen;

    let browser = 'Unknown';
    let browserVersion = '';
    if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edge') === -1) {
      browser = 'Chrome';
      browserVersion = ua.match(/Chrome\/([0-9.]+)/)?.[1] || '';
    } else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
      browser = 'Safari';
      browserVersion = ua.match(/Version\/([0-9.]+)/)?.[1] || '';
    } else if (ua.indexOf('Firefox') > -1) {
      browser = 'Firefox';
      browserVersion = ua.match(/Firefox\/([0-9.]+)/)?.[1] || '';
    } else if (ua.indexOf('Edge') > -1) {
      browser = 'Edge';
      browserVersion = ua.match(/Edge\/([0-9.]+)/)?.[1] || '';
    }

    let os = 'Unknown';
    let osVersion = '';
    if (ua.indexOf('Win') > -1) {
      os = 'Windows';
      osVersion = ua.match(/Windows NT ([0-9.]+)/)?.[1] || '';
    } else if (ua.indexOf('Mac') > -1) {
      os = 'macOS';
      osVersion = ua.match(/Mac OS X ([0-9._]+)/)?.[1]?.replace(/_/g, '.') || '';
    } else if (ua.indexOf('Linux') > -1) {
      os = 'Linux';
    } else if (ua.indexOf('Android') > -1) {
      os = 'Android';
      osVersion = ua.match(/Android ([0-9.]+)/)?.[1] || '';
    } else if (ua.indexOf('iOS') > -1) {
      os = 'iOS';
      osVersion = ua.match(/OS ([0-9_]+)/)?.[1]?.replace(/_/g, '.') || '';
    }

    let deviceType = 'Desktop';
    if (/Mobile|Android|iPhone|iPad|iPod/i.test(ua)) {
      deviceType = 'Mobile';
    } else if (/Tablet|iPad/i.test(ua)) {
      deviceType = 'Tablet';
    }

    return {
      browser,
      browserVersion,
      os,
      osVersion,
      screenResolution: `${screen.width}x${screen.height}`,
      devicePixelRatio: window.devicePixelRatio,
      userAgent: ua,
      deviceType,
    };
  }
}

export const assessmentCaptureService = AssessmentCaptureService.getInstance();
